import {
  Component,
  DoCheck,
  EventEmitter,
  forwardRef,
  Injector,
  Input,
  OnInit,
  Output
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  NgControl
} from '@angular/forms';

declare type InputType = "number" | "tel" | "text" | "email" | "password";

@Component({
  selector: 'form-input',
  templateUrl: './input.component.html',
  styleUrls: ['./input.component.css'],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    multi: true,
    useExisting: forwardRef(() => InputComponent)
  }]
})
export class InputComponent implements OnInit, ControlValueAccessor, DoCheck {

  @Input() placeholder: string = '';
  @Input() prepend!: string;
  @Input() append!: string;
  @Input() max: number | null = null;
  @Input() prependHtml!: string;
  @Input() appendHtml!: string;
  @Input() value: string | number = '';
  @Input() required: boolean = false;
  @Input() disabled: boolean = false;
  @Input() background: string = '';

  @Output() change: EventEmitter<string | number> = new EventEmitter();
  @Output() appendClick: EventEmitter<Event> = new EventEmitter();
  @Output() prependClick: EventEmitter<Event> = new EventEmitter();

  @Input() set type(type: InputType) {
    this._type = type;
    this.isPassword = type === "password";
    if (!this.append && this.isPassword) {
      this.append = (this._type == 'password') ? 'feather-eye-off' : 'feather-eye';
    }
  }

  isValid: boolean = true;
  errors: Array<any> = [];
  isPassword: boolean = false;
  _type: InputType = "text";

  ngControl: NgControl | null = null;

  private _onChange: any = () => {};
  private _onTouched: any = () => {};

  constructor(private injector: Injector) {}

  ngOnInit(): void {
    this.ngControl = this.injector.get(NgControl, null);
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  onAppendClick($event: Event) {
    if (this.isPassword) {
      this._type = (this._type === 'password') ? 'text' : 'password';
      this.append = (this._type == 'password') ? 'feather-eye-off' : 'feather-eye';
    }
    this.appendClick.emit($event);
  }

  onPrependClick($event: Event) {
    this.prependClick.emit($event);
  }

  onChange(value: string | number) {
    this.value = value;
    this.change.emit(value);
    this._onChange(value);
  }

  onInputChange($event: any): void {
    this.value = $event.target.value;
    this.change.emit(this.value);
    this._onChange(this.value);
  }

  onBlur(): void {
    this._onTouched();
  }

  onKeyDown(event: KeyboardEvent) {
    // Limite max caractères
    if (this.max) {
      if ((String(this.value).length + 1 > this.max) && !['Delete', 'Backspace'].includes(event.key)) {
        event.preventDefault();
        return;
      }
    }

    if (this._type === "number" || this._type === "tel") {
      const allowedKeys = [
        'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
        'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
        'Home', 'End'
      ];

      // Autoriser les raccourcis clavier (Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X)
      if (allowedKeys.includes(event.key) || event.ctrlKey || event.metaKey) {
        return;
      }

      // Autoriser seulement les chiffres 0-9
      if (!/^\d$/.test(event.key)) {
        event.preventDefault();
      }
    }
  }

  writeValue(value: string | number): void {
    this.value = value || '';
  }

  registerOnChange(fn: any): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this._onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  ngDoCheck(): void {
    if ((this.value || '').toString().length == 0 && this.required) {
      this.errors = ['required'];
    } else {
      this.errors = [];
    }
  }

  get showInvalid(): boolean {
    return !!(
      this.ngControl?.control?.invalid &&
      (this.ngControl?.control?.touched || this.ngControl?.control?.dirty)
    );
  }

  get type(): InputType {
    return this._type;
  }
}